import React, { useEffect, useMemo, useState } from "react";
import { http, ADMIN_TOKEN_KEY } from "../lib/api";
import { toast } from "sonner";
import {
  Search, CheckCircle2, Clock, FileSpreadsheet,
  Paperclip, Mail, Phone, Trash2, RotateCcw,
} from "lucide-react";
import { useNotifications } from "../hooks/useNotifications";

const LEAD_STATUSES = [
  { value: "nuevo",           label: "Nuevo",           color: "#b45309", bg: "rgba(245,158,11,0.15)" },
  { value: "en_cotizacion",   label: "En cotización",   color: "#1e40af", bg: "rgba(59,130,246,0.15)" },
  { value: "en_negociacion",  label: "En negociación",  color: "#7c2d12", bg: "rgba(234,88,12,0.15)" },
  { value: "convertido",      label: "Convertido",      color: "#15803d", bg: "rgba(34,197,94,0.15)" },
  { value: "perdido",         label: "Perdido",         color: "#991b1b", bg: "rgba(239,68,68,0.15)" },
];

export default function AdminFormularioWeb() {
  const [items, setItems] = useState([]);
  const [trash, setTrash] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("todos"); // todos | nuevo | en_cotizacion | en_negociacion | convertido | perdido | borrados
  const [q, setQ] = useState("");
  const { refresh: refreshNotifs } = useNotifications();

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: active }, { data: deleted }] = await Promise.all([
        http.get("/admin/formularios-web"),
        http.get("/admin/formularios-web", { params: { deleted: true } }),
      ]);
      setItems(active || []);
      setTrash(deleted || []);
    } catch {
      toast.error("No se pudo cargar el listado");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const base = { todos: items.length, borrados: trash.length };
    for (const s of LEAD_STATUSES) {
      base[s.value] = items.filter((c) => (c.lead_status || "nuevo") === s.value).length;
    }
    return base;
  }, [items, trash]);

  const filtered = useMemo(() => {
    const source = tab === "borrados" ? trash : items;
    const ql = q.trim().toLowerCase();
    return source.filter((r) => {
      const ls = r.lead_status || "nuevo";
      if (tab !== "todos" && tab !== "borrados" && ls !== tab) return false;
      if (!ql) return true;
      return [
        r.razon_social, r.rut_comunidad, r.nombre, r.email,
        r.telefono, r.direccion, r.comuna_ciudad, r.region,
        r.partner,
      ].filter(Boolean).some((v) => String(v).toLowerCase().includes(ql));
    });
  }, [items, trash, tab, q]);

  const setLeadStatus = async (id, lead_status) => {
    try {
      await http.patch(`/admin/formularios-web/${id}/estado`, { lead_status });
      setItems((prev) => prev.map((r) => r.id === id ? {
        ...r,
        lead_status,
        convertido: lead_status === "convertido",
        comision_estado: lead_status === "convertido" ? (r.comision_estado === "cobrada" ? "cobrada" : "pendiente") : "no_aplica",
      } : r));
      refreshNotifs();
      const label = LEAD_STATUSES.find((s) => s.value === lead_status)?.label || lead_status;
      toast.success(`Estado actualizado: ${label}`);
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error("Esta fila no tiene solicitud asociada");
      } else {
        toast.error("No se pudo actualizar el estado");
      }
    }
  };

  const deleteLead = async (r) => {
    if (!window.confirm(`¿Mover a Borrados el lead de ${r.razon_social || r.nombre || "esta fila"}?`)) return;
    try {
      await http.delete(`/admin/formularios-web/${r.id}`);
      setItems((prev) => prev.filter((x) => x.id !== r.id));
      setTrash((prev) => [{ ...r, deleted_at: new Date().toISOString() }, ...prev]);
      refreshNotifs();
      toast.success("Movido a Borrados");
    } catch {
      toast.error("No se pudo borrar");
    }
  };

  const restoreLead = async (r) => {
    try {
      await http.post(`/admin/formularios-web/${r.id}/restore`);
      setTrash((prev) => prev.filter((x) => x.id !== r.id));
      const restored = { ...r };
      delete restored.deleted_at;
      setItems((prev) => [restored, ...prev]);
      refreshNotifs();
      toast.success("Lead restaurado");
    } catch {
      toast.error("No se pudo restaurar");
    }
  };

  const hardDeleteLead = async (r) => {
    if (!window.confirm(`⚠️ Borrar PERMANENTEMENTE el lead de ${r.razon_social || r.nombre || "esta fila"}?\n\nEsta acción NO se puede deshacer.`)) return;
    try {
      await http.delete(`/admin/formularios-web/${r.id}/permanent`);
      setTrash((prev) => prev.filter((x) => x.id !== r.id));
      toast.success("Lead borrado permanentemente");
    } catch {
      toast.error("No se pudo borrar permanentemente");
    }
  };

  const downloadFile = async (url, filename) => {
    try {
      const token = localStorage.getItem(ADMIN_TOKEN_KEY);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${url}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      const objUrl = URL.createObjectURL(blob);
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (e) {
      toast.error("No se pudo descargar");
    }
  };

  const downloadCsv = () => downloadFile(
    "/admin/formularios-web/export",
    `central-leads-${new Date().toISOString().slice(0,10)}.csv`
  );

  const downloadPoliza = (solId, name) => downloadFile(
    `/admin/formularios-web/${solId}/poliza`,
    name || `poliza-${solId}`
  );

  const downloadCarta = async (cartaId, razon) => {
    try {
      const res = await http.post(`/cartas-asignacion/${cartaId}/pdf`, {}, { responseType: "blob" });
      const a = document.createElement("a");
      const url = URL.createObjectURL(res.data);
      a.href = url;
      a.download = `carta-${(razon || cartaId).replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar la carta");
    }
  };

  return (
    <div data-testid="admin-central-leads">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>
            Central de Leads
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            Todos los leads recibidos desde el sitio y sus co-brandings. Gestiona el pipeline: Nuevo → Cotización → Negociación → Convertido / Perdido.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="relative w-full md:w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-soft)" }}/>
            <input
              type="text"
              placeholder="Buscar nombre, comunidad, RUT, email, origen…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="field-input field-input--compact w-full pl-9"
              data-testid="leads-search"
            />
          </div>
          <button onClick={downloadCsv} className="btn-primary" style={{ padding: "0.7rem 1.1rem", fontSize: 13.5 }} data-testid="leads-export">
            <FileSpreadsheet size={15}/> Descargar CSV
          </button>
        </div>
      </div>

      {/* Filter tabs by lead_status */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { value: "todos", label: "Todos", color: "var(--brand-navy)" },
          ...LEAD_STATUSES,
          { value: "borrados", label: "Borrados", color: "#6b7280" },
        ].map((t) => {
          const active = tab === t.value;
          return (
            <button
              key={t.value}
              onClick={() => setTab(t.value)}
              className="px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold transition"
              style={{
                background: active ? t.color : "white",
                color: active ? "white" : "var(--text-secondary)",
                border: `1px solid ${active ? t.color : "var(--border-medium)"}`,
              }}
              data-testid={`leads-tab-${t.value}`}
            >
              {t.label} <span className="opacity-70 ml-1">({counts[t.value] ?? 0})</span>
            </button>
          );
        })}
      </div>

      <div className="bg-white border rounded-xl overflow-hidden" style={{ borderColor: "var(--border-subtle)" }}>
        {loading ? (
          <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>No hay registros que mostrar.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Origen</th>
                  <th>Comunidad</th>
                  <th>Representante</th>
                  <th>Contacto</th>
                  <th>Dirección</th>
                  <th>Póliza</th>
                  <th>Carta firmada</th>
                  <th>Fecha</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const ls = r.lead_status || "nuevo";
                  const status = LEAD_STATUSES.find((s) => s.value === ls) || LEAD_STATUSES[0];
                  const isNew = ls === "nuevo";
                  return (
                  <tr key={r.id} style={isNew ? { background: "rgba(245,158,11,0.04)" } : undefined} data-testid={`lead-row-${r.id}`}>
                    <td>
                      <select
                        value={ls}
                        onChange={(e) => setLeadStatus(r.id, e.target.value)}
                        disabled={tab === "borrados"}
                        className="text-[11.5px] font-bold px-2 py-1 rounded border cursor-pointer uppercase tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: status.bg, color: status.color, borderColor: status.color + "40" }}
                        data-testid={`lead-status-select-${r.id}`}
                      >
                        {LEAD_STATUSES.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {r.partner ? (
                        <span className="text-[10.5px] font-bold uppercase tracking-wide px-2 py-1 rounded" style={{ background: "rgba(30,63,174,0.1)", color: "var(--brand-navy)" }} data-testid={`lead-origen-${r.id}`}>
                          {String(r.partner).toUpperCase()}
                        </span>
                      ) : (
                        <span className="text-[10.5px] font-semibold uppercase tracking-wide px-2 py-1 rounded" style={{ background: "var(--bg-soft)", color: "var(--text-muted)" }} data-testid={`lead-origen-${r.id}`}>
                          Directo
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="font-semibold">{r.razon_social || "—"}</div>
                      <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{r.rut_comunidad || ""}</div>
                    </td>
                    <td>
                      <div className="font-semibold">{r.nombre}</div>
                      <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                        {r.cargo}{r.rut_representante ? ` · ${r.rut_representante}` : ""}
                      </div>
                    </td>
                    <td>
                      <div className="text-[12.5px] inline-flex items-center gap-1"><Mail size={11}/> {r.email}</div>
                      <div className="text-[12.5px] inline-flex items-center gap-1 mt-0.5" style={{ color: "var(--text-muted)" }}><Phone size={11}/> {r.telefono}</div>
                    </td>
                    <td className="text-[13px]">
                      {r.direccion || "—"}
                      <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                        {[r.comuna_ciudad, r.region].filter(Boolean).join(" · ")}
                      </div>
                    </td>
                    <td>
                      {r.tiene_archivo_poliza ? (
                        <button
                          onClick={() => downloadPoliza(r.id, r.poliza_archivo_nombre)}
                          className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-2 py-1 rounded-md transition hover:opacity-80"
                          style={{ background: "rgba(238,81,71,0.1)", color: "var(--brand-accent)" }}
                          data-testid={`pol-${r.id}`}
                        >
                          <Paperclip size={11}/> Descargar
                        </button>
                      ) : (
                        <span className="text-[12px]" style={{ color: "var(--text-muted)" }}>—</span>
                      )}
                      {r.vencimiento_poliza && (
                        <div className="text-[10.5px] mt-1" style={{ color: "var(--text-muted)" }}>{r.vencimiento_poliza}</div>
                      )}
                    </td>
                    <td>
                      {r.firmada ? (
                        <button
                          onClick={() => downloadCarta(r.carta_id, r.razon_social)}
                          className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-2 py-1 rounded-md transition hover:opacity-80"
                          style={{ background: "rgba(34,197,94,0.1)", color: "#15803d" }}
                          data-testid={`carta-${r.carta_id}`}
                        >
                          <CheckCircle2 size={11}/> PDF
                        </button>
                      ) : (
                        <div className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-2 py-1 rounded-md" style={{ background: "rgba(245,158,11,0.1)", color: "#b45309" }}>
                          <Clock size={11}/> Pendiente
                        </div>
                      )}
                    </td>
                    <td className="text-[12px]" style={{ color: "var(--text-muted)" }}>
                      {(r.fecha || "").slice(0, 10)}
                    </td>
                    <td>
                      {tab === "borrados" ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => restoreLead(r)}
                            className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2 py-1 rounded hover:opacity-80"
                            style={{ background: "rgba(30,63,174,0.1)", color: "var(--brand-navy)" }}
                            data-testid={`lead-restore-${r.id}`}
                            title="Restaurar"
                          >
                            <RotateCcw size={12}/> Restaurar
                          </button>
                          <button
                            onClick={() => hardDeleteLead(r)}
                            className="inline-flex items-center justify-center p-1.5 rounded hover:bg-red-50 transition"
                            style={{ color: "#991b1b" }}
                            data-testid={`lead-hard-delete-${r.id}`}
                            title="Borrar permanentemente (no se puede deshacer)"
                          >
                            <Trash2 size={14}/>
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => deleteLead(r)}
                          className="inline-flex items-center justify-center p-1.5 rounded hover:bg-red-50 transition"
                          style={{ color: "#991b1b" }}
                          data-testid={`lead-delete-${r.id}`}
                          title="Mover a Borrados"
                        >
                          <Trash2 size={14}/>
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
